/**
 * Formatos GA restantes (GA-Bitacoras.pdf y GA_Inventario.pdf):
 *   · Bitácora de incidencia
 *   · Bitácora de preferencia médica
 *   · Control de inventario
 *
 * Mismo criterio que ga-forms.ts: funciones puras que reciben los datos ya
 * resueltos (los calcula el API en /gaForms/*, recortados por rol y alcance)
 * y regresan la definición pdfmake. La descarga la hace GaPdfService.
 */
import { GA, cotColor } from './ga-forms';

const LANDSCAPE = { width: 720, height: 540 };
const PORTRAIT = { width: 540, height: 720 };
const s = (x: any) => (x === null || x === undefined) ? '' : String(x);

/**
 * Texto en posición fija con ancho propio. En pdfmake el `width` de un texto
 * con absolutePosition se ignora (centra contra la página), así que se envuelve
 * en una columna de ese ancho.
 */
function at(x: number, y: number, width: number, cell: any): any {
  return { absolutePosition: { x, y }, columns: [{ width, ...cell }] };
}

// ── Tipos (los mismos que regresa el API) ────────────────────────────────

export interface GaIncidentData {
  registro: string; hospital: string; surgeon: string; nurse: string; folio: string;
  technician: string; surgery: string; date: string; hour: string;
  tags: string[];
  items: { code: string; description: string; serial: string; quantity: number }[];
  description: string[];
}

export interface GaPreferenceItem { code: string; description: string; line: string; quantity: number; share: number; }
export interface GaPreferenceSurgery { surgery: string; folios: number; lastDate: string; items: GaPreferenceItem[]; omitted: number; }
export interface GaPreferenceData {
  doctor: string; hospital: string; folios: number; rule: string;
  surgeries: GaPreferenceSurgery[]; omittedSurgeries: number;
}

export interface GaInventoryValues { received: number; consumption: number; dlv: number; delivered: number | null; }
export interface GaInventoryBlock { key: string; label: string; state: 'closed' | 'open' | 'future'; start: string; end: string; dlvFolios: string[]; }
export interface GaInventoryData {
  hospital: string; date: string; generatedAt: string;
  blocks: GaInventoryBlock[];
  rows: { line: string; code: string; description: string; values: (GaInventoryValues | null)[] }[];
}

// ── Piezas comunes ───────────────────────────────────────────────────────

/** Barra superior y franja inferior de las hojas horizontales de bitácora. */
function landscapeFrame(title: string): any[] {
  return [
    { canvas: [
      { type: 'rect', x: 0, y: 0, w: 720, h: 22, color: GA.blue },
      { type: 'rect', x: 0, y: 522, w: 720, h: 18, color: GA.blue },
    ], absolutePosition: { x: 0, y: 0 } },
    { text: title, absolutePosition: { x: 10, y: 6 }, color: GA.white, bold: true, fontSize: 9 },
    { text: 'Medtronic', absolutePosition: { x: 640, y: 4 }, color: GA.white, bold: true, fontSize: 11 },
  ];
}

/** Etiqueta + valor + línea de captura en posición fija. */
function lineField(label: string, value: string, x: number, y: number, lineX: number, lineW: number, size = 8): any[] {
  return [
    { text: label, absolutePosition: { x, y }, color: GA.label, fontSize: size },
    at(lineX + 2, y - 2, lineW - 4, { text: value || ' ', bold: true, fontSize: 8, noWrap: true }),
    { canvas: [{ type: 'line', x1: lineX, y1: y + 9, x2: lineX + lineW, y2: y + 9, lineWidth: 1, lineColor: GA.line }],
      absolutePosition: { x: 0, y: 0 } },
  ];
}

function registroBox(value: string, x: number, y: number): any[] {
  return [
    { text: 'REGISTRO:', absolutePosition: { x: x - 56, y: y + 8 }, color: GA.line, fontSize: 8 },
    { canvas: [{ type: 'rect', x, y, w: 62, h: 26, lineWidth: 1.2, lineColor: GA.line }], absolutePosition: { x: 0, y: 0 } },
    at(x, y + 8, 62, { text: value || ' ', alignment: 'center', bold: true, fontSize: 9 }),
  ];
}

const gridLayout = (color = GA.line) => ({
  hLineWidth: () => 0.6, vLineWidth: () => 0.6, hLineColor: () => color, vLineColor: () => color,
  paddingLeft: () => 2, paddingRight: () => 2, paddingTop: () => 1.5, paddingBottom: () => 0,
});

// ── BITÁCORA DE INCIDENCIA (GA-Bitacoras.pdf, página 7) ──────────────────

export function buildIncidentDoc(d: GaIncidentData): any {
  const content: any[] = [...landscapeFrame('BITACORA')];
  content.push({ text: 'INCIDENCIA', absolutePosition: { x: 18, y: 42 }, bold: true, color: GA.navy, fontSize: 10 });
  content.push(...registroBox(d.registro, 650, 34));

  const labels: [string, string][] = [
    ['Nombre del Cirujano', d.surgeon], ['Nombre de Enfermera', d.nurse], ['Folio de Cirugía', d.folio],
    ['Nombre del Técnico', d.technician], ['Cirugía', d.surgery],
  ];
  labels.forEach(([l, v], i) => content.push(...lineField(l, v, 42, 70 + i * 18, 146, 230)));
  content.push(...lineField('Fecha', d.date, 384, 70, 452, 76));
  content.push(...lineField('Hora', d.hour, 384, 88, 452, 76));

  // Etiquetas (RFID de la pieza).
  content.push({ canvas: [{ type: 'rect', x: 469, y: 124, w: 225, h: 119, lineWidth: 1, lineColor: GA.navy }], absolutePosition: { x: 0, y: 0 } });
  content.push({ text: 'Etiquetas', absolutePosition: { x: 475, y: 128 }, color: GA.navy, fontSize: 9 });
  content.push({ stack: (d.tags.length ? d.tags : [' ']).map(t => ({ text: t, fontSize: 8, bold: true, margin: [0, 0, 0, 3] })),
    absolutePosition: { x: 475, y: 144 } });

  // Pieza(s): código, descripción, serie o lote, cantidad.
  const head = ['CODIGO', 'Descripción', 'No Serie ó Lote', 'Cantidad'].map(t => ({ text: t, bold: true, color: GA.white, fillColor: GA.blue, fontSize: 5.5 }));
  const body: any[] = [head];
  for (const it of d.items.slice(0, 4)) {
    body.push([{ text: it.code, fontSize: 7 }, { text: it.description, fontSize: 6.5 }, { text: it.serial, fontSize: 7 },
      { text: s(it.quantity), fontSize: 7, alignment: 'center' }]);
  }
  for (let i = body.length - 1; i < 4; i++) body.push([' ', ' ', ' ', ' '].map(t => ({ text: t, fontSize: 7 })));
  content.push({ absolutePosition: { x: 42, y: 176 },
    table: { widths: [83, 151, 79, 79], heights: (i: number) => (i === 0 ? 9 : 12), body }, layout: gridLayout(GA.navy) });

  // Descripción de la incidencia: recuadro con renglones.
  const boxX = 37, boxY = 254, boxW = 647, boxH = 168;
  const rules = Array.from({ length: 10 }, (_, i) => ({ type: 'line', x1: boxX, y1: boxY + 22 + i * 14.5, x2: boxX + boxW, y2: boxY + 22 + i * 14.5, lineWidth: 0.6, lineColor: GA.line }));
  content.push({ canvas: [{ type: 'rect', x: boxX, y: boxY, w: boxW, h: boxH, lineWidth: 1.8, lineColor: GA.navy }, ...rules], absolutePosition: { x: 0, y: 0 } });
  content.push({ text: 'DESCRIPCIÓN DE INCIDENCIA', absolutePosition: { x: boxX + 6, y: boxY + 6 }, color: GA.line, fontSize: 8.5 });
  content.push(at(boxX + 6, boxY + 24, boxW - 12, { stack: d.description.map(t => ({ text: t, fontSize: 8.5, lineHeight: 1.46 })) }));

  // Firmas.
  const sigs: [string, string, number][] = [['Técnico', d.technician, 36], ['Cirujano', d.surgeon, 269], ['Enfermera', d.nurse, 502]];
  for (const [role, name, x] of sigs) {
    content.push(at(x, 478, 182, { text: name || ' ', alignment: 'center', fontSize: 7.5, bold: true }));
    content.push({ canvas: [{ type: 'line', x1: x, y1: 491, x2: x + 182, y2: 491, lineWidth: 1.2, lineColor: GA.line }], absolutePosition: { x: 0, y: 0 } });
    content.push(at(x, 494, 182, { text: `Nombre y Firma\n${role}`, alignment: 'center', color: GA.line, fontSize: 6.5 }));
  }

  return {
    pageSize: LANDSCAPE, pageMargins: [0, 0, 0, 0],
    defaultStyle: { fontSize: 8, color: '#111' },
    info: { title: `Bitácora de incidencia ${d.registro}` },
    content,
  };
}

// ── BITÁCORA DE PREFERENCIA MÉDICA (GA-Bitacoras.pdf, página 5) ──────────

function preferenceTable(sg: GaPreferenceSurgery | undefined): any {
  const head = ['CODIGO', 'DESCRIPCION', 'CANT'].map(t => ({ text: t, color: GA.white, fillColor: GA.line, fontSize: 4.5, alignment: t === 'CANT' ? 'center' : 'left' }));
  const body: any[] = [head];
  const items = sg?.items ?? [];
  for (const it of items) {
    body.push([
      { text: it.code, fontSize: 5.5, color: cotColor(it.line) === GA.label ? '#111' : cotColor(it.line), bold: true },
      { text: it.description, fontSize: 5, noWrap: true },
      { text: s(it.quantity), fontSize: 6, alignment: 'center', bold: true },
    ]);
  }
  for (let i = items.length; i < 11; i++) body.push([{ text: ' ', fontSize: 5.5 }, ' ', ' ']);
  return { table: { widths: [36, 96, 18], heights: (i: number) => (i === 0 ? 8 : 10.9), body }, layout: gridLayout(GA.line) };
}

function preferenceBlock(doctor: string, surgeries: (GaPreferenceSurgery | undefined)[], y: number): any[] {
  const out: any[] = [...lineField('Nombre del Medico', doctor, 10, y, 102, 290, 7.5)];
  surgeries.forEach((sg, i) => {
    const x = 22 + i * 170;
    const name = sg ? `${sg.surgery}` : '';
    out.push(...lineField('Cirugía', name, x, y + 24, x + 40, 120, 7.5));
    if (sg) {
      out.push({ text: `${sg.folios} folio${sg.folios === 1 ? '' : 's'} · último ${sg.lastDate}${sg.omitted ? ` · +${sg.omitted} códigos` : ''}`,
        absolutePosition: { x: x + 40, y: y + 35 }, color: GA.label, fontSize: 5 });
    }
    out.push({ absolutePosition: { x, y: y + 42 }, ...preferenceTable(sg) });
  });
  return out;
}

export function buildPreferenceDoc(d: GaPreferenceData): any {
  const pages: any[][] = [];
  const list = d.surgeries;
  // Dos bloques de 4 cirugías por hoja (como el formato).
  for (let start = 0; start < Math.max(list.length, 1); start += 8) {
    const page: any[] = [...landscapeFrame('BITACORA PREFERENCIA MÉDICA')];
    const first = list.slice(start, start + 4);
    const second = list.slice(start + 4, start + 8);
    const pad = (arr: GaPreferenceSurgery[]) => [0, 1, 2, 3].map(i => arr[i]);
    page.push(...preferenceBlock(d.doctor, pad(first), 32));
    page.push(...preferenceBlock(second.length ? d.doctor : '', pad(second), 262));
    page.push(at(22, 506, 560, { text: `${d.hospital} · calculado de ${d.folios} folio${d.folios === 1 ? '' : 's'}. ${d.rule}`, color: GA.label, fontSize: 6 }));
    page.push({ text: 'HOJA:', absolutePosition: { x: 596, y: 506 }, color: GA.line, fontSize: 8 });
    page.push({ canvas: [{ type: 'rect', x: 628, y: 496, w: 62, h: 22, lineWidth: 1, lineColor: GA.line }], absolutePosition: { x: 0, y: 0 } });
    page.push(at(628, 503, 62, { text: String(pages.length + 1), alignment: 'center', bold: true, fontSize: 9 }));
    pages.push(page);
  }
  const content: any[] = [];
  pages.forEach((p, i) => { if (i) content.push({ text: '', pageBreak: 'before' }); content.push(...p); });
  return {
    pageSize: LANDSCAPE, pageMargins: [0, 0, 0, 0],
    defaultStyle: { fontSize: 8, color: '#111' },
    info: { title: `Bitácora de preferencia médica ${d.doctor}` },
    content,
  };
}

// ── CONTROL DE INVENTARIO (GA_Inventario.pdf) ────────────────────────────

function inventoryCover(hospital: string, bitacora: string): any[] {
  return [
    { canvas: [
      { type: 'rect', x: 0, y: 0, w: 540, h: 140, color: GA.blue },
      { type: 'rect', x: 339, y: 0, w: 201, h: 140, color: GA.navy },
      { type: 'rect', x: 432, y: 237, w: 77, h: 24, lineWidth: 1.2, lineColor: GA.line },
      { type: 'line', x1: 142, y1: 525, x2: 481, y2: 525, lineWidth: 2.5, lineColor: GA.line },
      { type: 'rect', x: 0, y: 617, w: 540, h: 103, color: GA.blue },
    ], absolutePosition: { x: 0, y: 0 } },
    { text: 'Medtronic', absolutePosition: { x: 368, y: 50 }, color: GA.white, bold: true, fontSize: 26 },
    { text: 'CONTROL DE INVENTARIO', absolutePosition: { x: 54, y: 186 }, color: '#44546A', bold: true, fontSize: 28 },
    { text: 'BITACORA:', absolutePosition: { x: 390, y: 245 }, color: GA.line, fontSize: 6.5 },
    at(432, 244, 77, { text: bitacora, alignment: 'center', bold: true, fontSize: 9 }),
    { text: 'HOSPITAL:', absolutePosition: { x: 61, y: 508 }, color: GA.navy, fontSize: 15 },
    { text: hospital, absolutePosition: { x: 146, y: 509 }, bold: true, fontSize: 13 },
  ];
}

function inventoryPanel(b: GaInventoryBlock | undefined, date: string, y: number, accent: string): any[] {
  const x = 436, w = 98, h = 300;
  const out: any[] = [
    { canvas: [{ type: 'rect', x, y, w, h, lineWidth: 1.2, lineColor: accent }], absolutePosition: { x: 0, y: 0 } },
  ];
  const field = (label: string, value: string, yy: number) => [
    { text: label, absolutePosition: { x: x + 8, y: yy }, color: GA.line, fontSize: 6.5 },
    at(x + 36, yy - 1, w - 40, { text: value || ' ', bold: true, fontSize: 7 }),
    { canvas: [{ type: 'line', x1: x + 34, y1: yy + 8, x2: x + w - 4, y2: yy + 8, lineWidth: 0.8, lineColor: GA.blue }], absolutePosition: { x: 0, y: 0 } },
  ];
  const startTime = b ? b.start.split(' ')[1] ?? '' : '';
  const endDay = b ? b.end.split(' ')[0] : '';
  const endTime = b && b.state === 'closed' ? (b.end.split(' ')[1] ?? '') : '';
  out.push({ text: b?.label ?? ' ', absolutePosition: { x: x + 8, y: y + 6 }, color: accent, fontSize: 6, bold: true });
  out.push({ text: 'RECIBO', absolutePosition: { x: x + 8, y: y + 16 }, color: GA.navy, bold: true, fontSize: 9 });
  out.push(...field('Fecha', b ? date : '', y + 34), ...field('Hora', startTime, y + 50), ...field('Firma', '', y + 78));
  out.push({ text: 'CONSUMO + DLV\n+ ENTREGO', absolutePosition: { x: x + 8, y: y + 104 }, color: GA.navy, bold: true, fontSize: 8 });
  const endDate = b && b.state === 'closed' ? (endDay === date.slice(0, 5) ? date : `${endDay}/${date.slice(6)}`) : '';
  out.push(...field('Fecha', endDate, y + 134), ...field('Hora', endTime, y + 150), ...field('Firma', '', y + 178));
  const dlv = b?.dlvFolios ?? [];
  const dlvField = (label: string, value: string, yy: number) => [
    { text: label, absolutePosition: { x: x + 8, y: yy }, color: GA.line, fontSize: 6 },
    at(x + 8, yy + 9, w - 14, { text: value || ' ', bold: true, fontSize: 6.5 }),
    { canvas: [{ type: 'line', x1: x + 8, y1: yy + 18, x2: x + w - 6, y2: yy + 18, lineWidth: 0.8, lineColor: GA.blue }], absolutePosition: { x: 0, y: 0 } },
  ];
  out.push(...dlvField('# DLV 1 recibido', dlv[0] ?? '', y + 214), ...dlvField('# DLV 2 recibido', dlv.slice(1).join(', '), y + 248));
  return out;
}

export function buildInventoryControlDoc(d: GaInventoryData): any {
  const content: any[] = [...inventoryCover(d.hospital, d.date), { text: '', pageBreak: 'after' }];
  const top = 56, bottom = 704;
  // Cada renglón ocupa su alto + 0.4 de línea; mínimo legible 4.8 pt.
  const maxRows = Math.floor((bottom - top) / 5.2) - 1;
  const chunks: typeof d.rows[] = [];
  for (let i = 0; i < Math.max(d.rows.length, 1); i += maxRows) chunks.push(d.rows.slice(i, i + maxRows));

  const colA = GA.blue, colB = '#8E8CF0', headDark = '#1B1A4A';
  const num = (v: number | null | undefined, keepZero: boolean) =>
    v === null || v === undefined ? '' : (v === 0 && !keepZero ? '' : String(v));

  chunks.forEach((rows, page) => {
    if (page) content.push({ text: '', pageBreak: 'before' });
    content.push({ canvas: [{ type: 'rect', x: 0, y: 0, w: 540, h: 38, color: GA.blue },
      { type: 'rect', x: 452, y: 6, w: 82, h: 26, lineWidth: 2.5, lineColor: GA.navy, color: GA.white }], absolutePosition: { x: 0, y: 0 } });
    content.push({ text: 'Medtronic', absolutePosition: { x: 12, y: 11 }, color: GA.white, bold: true, fontSize: 14 });
    content.push(at(452, 14, 82, { text: d.date, alignment: 'center', bold: true, fontSize: 9 }));
    content.push(at(120, 13, 320, { text: d.hospital, color: GA.white, fontSize: 10, noWrap: true }));

    const rowH = Math.max(4.8, Math.min(11, (bottom - top) / (rows.length + 1) - 0.45));
    const fs = Math.max(3.6, Math.min(6.5, rowH * 0.62));
    const headCell = (t: string, fill: string, align = 'center') => ({ text: t, bold: true, color: GA.white, fillColor: fill, fontSize: Math.min(5.5, fs + 0.5), alignment: align });
    const head = [headCell('', headDark), headCell('CÓDIGO', headDark, 'left'), headCell('DESCRIPCIÓN', headDark, 'left'),
      ...['RECIBIDO', 'CONSUMO', 'DLV', 'ENTREGO'].map(t => headCell(t, colA)),
      ...['RECIBIDO', 'CONSUMO', 'DLV', 'ENTREGO'].map(t => headCell(t, colB))];
    const body: any[] = [head];
    for (const r of rows) {
      const line = r.line || 'Otros';
      const cells: any[] = [
        { text: line.slice(0, 5), bold: true, color: GA.white, fillColor: cotColor(line) === GA.label ? GA.otros : cotColor(line), fontSize: fs - 0.6, alignment: 'center' },
        { text: r.code, fontSize: fs, noWrap: true },
        { text: r.description, fontSize: fs - 0.3, noWrap: true },
      ];
      for (let bi = 0; bi < 2; bi++) {
        const v = r.values[bi] ?? null;
        cells.push(
          { text: num(v?.received, true), fontSize: fs, alignment: 'center' },
          { text: num(v?.consumption, false), fontSize: fs, alignment: 'center', bold: true },
          { text: num(v?.dlv, false), fontSize: fs, alignment: 'center', bold: true },
          { text: num(v?.delivered ?? null, true), fontSize: fs, alignment: 'center' },
        );
      }
      body.push(cells);
    }
    if (!rows.length) body.push([{ text: 'Sin piezas en el hospital para este día.', colSpan: 11, fontSize: 7, color: GA.label }, ...Array(10).fill('')]);
    content.push({
      absolutePosition: { x: 8, y: top },
      table: { headerRows: 1, widths: [16, 46, 82, 30, 30, 30, 30, 30, 30, 30, 30], heights: () => rowH, body },
      layout: {
        hLineWidth: () => 0.4, vLineWidth: (i: number) => (i === 7 ? 1.4 : 0.4),
        hLineColor: () => GA.grid, vLineColor: (i: number) => (i === 7 ? GA.navy : GA.grid),
        paddingLeft: () => 1, paddingRight: () => 1, paddingTop: () => Math.max(0, (rowH - fs) / 2 - 1), paddingBottom: () => 0,
      },
    });

    content.push(...inventoryPanel(d.blocks[0], d.date, 72, GA.navy));
    content.push(...inventoryPanel(d.blocks[1], d.date, 392, colB));
    content.push(at(8, 708, 524, { text: `Generado por Epione el ${d.generatedAt}. Recibido y entregó = piezas disponibles en el hospital al inicio y al final del turno; consumo = piezas en folios del turno; DLV = piezas que entraron en el turno.`, color: GA.label, fontSize: 5 }));
  });

  return {
    pageSize: PORTRAIT, pageMargins: [0, 0, 0, 0],
    defaultStyle: { fontSize: 7, color: '#111' },
    info: { title: `Control de inventario ${d.hospital} ${d.date}` },
    content,
  };
}
